import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";

/** Classes of `Skeleton`: the palette-aware `.skeleton` shimmer in a given shape (`flat` fills a clipped media box). */
export const skeletonVariants = cva("skeleton block", {
  variants: {
    shape: {
      text: "h-4 rounded",
      block: "rounded-card",
      circle: "rounded-full",
      pill: "h-6 rounded-full",
      flat: "rounded-none",
    },
  },
  defaultVariants: { shape: "text" },
});

/** Props of `Skeleton`; size it with `className` (w-*, h-*). */
export type SkeletonProps = VariantProps<typeof skeletonVariants> & Omit<HTMLAttributes<HTMLSpanElement>, "children">;

/** Shimmer placeholder; decorative, so mark the loading region itself with `aria-busy`. */
export const Skeleton = forwardRef<HTMLSpanElement, SkeletonProps>(function Skeleton(
  { shape, className, ...rest },
  ref,
) {
  return <span {...rest} ref={ref} aria-hidden className={cn(skeletonVariants({ shape }), className)} />;
});

/** Props of `SkeletonText`. */
export type SkeletonTextProps = {
  lines?: number;
  className?: string;
};

/** Paragraph placeholder: `lines` text bars, the last one shorter. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <span aria-hidden className={cn("flex w-full flex-col gap-2", className)}>
      {Array.from({ length: lines }, (_, line) => (
        <Skeleton key={line} className={line === lines - 1 && lines > 1 ? "w-2/3" : "w-full"} />
      ))}
    </span>
  );
}
